import {
  PRODUCT_INFORMATION_CONSTANTS,
  CLAIM_INFORMATION_CONSTANTS,
  ADDITIONAL_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
} from "src/forms/IPL/constants";
import { FormStateType } from "src/forms/types";
import { CreateInternationalProductLiabilityClaimCommandInput } from "@amzn/ttechclaimintakeservice-client";
import {
  formatDateForBackend,
  getFieldValue,
  getBooleanFieldValue,
  buildAttachments,
  buildAddressObject,
} from "../helpers";
import { IPLClaimModelAdditionalFields } from "./types";

// Helper function to build claim reporter information
const buildClaimReporterInfo = (additionalFields: IPLClaimModelAdditionalFields) => ({
  amazonClaimReferenceId: additionalFields.amazonClaimReferenceId ?? undefined,
  claimReporterFirstName: additionalFields?.user?.givenName ?? undefined,
  claimReporterLastName: additionalFields?.user?.familyName ?? undefined,
  claimReporterAlias: additionalFields?.user?.id ?? undefined,
  claimReporterJobDescription: additionalFields?.user?.jobDescription ?? undefined,
  claimReporterEmailAddress: additionalFields?.user?.email ?? undefined,
});

// Helper function to build product information
const buildProductInfo = (formState: FormStateType) => ({
  itemPurchasedMarketplace: getFieldValue(
    formState,
    PRODUCT_INFORMATION_CONSTANTS.COUNTRY_WHERE_ITEM_WAS_PURCHASED
  ),
  orderId: getFieldValue(formState, PRODUCT_INFORMATION_CONSTANTS.ORDER_ID),
  amazonStandardIdentificationNumber: getFieldValue(formState, PRODUCT_INFORMATION_CONSTANTS.ASIN),
  productName: getFieldValue(formState, PRODUCT_INFORMATION_CONSTANTS.PRODUCT_NAME),
  fulfilmentPath: getFieldValue(formState, PRODUCT_INFORMATION_CONSTANTS.FULFILMENT_PATH),
});

// Helper function to build claim information
const buildClaimInfo = (formState: FormStateType) => ({
  incidentDate: formatDateForBackend(
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.DATE_OF_LOSS)
  ),
  customerFirstName: getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.CUSTOMER_FIRST_NAME),
  customerLastName: getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.CUSTOMER_LAST_NAME),
  insurerOrRepresentativeName: getFieldValue(
    formState,
    CLAIM_INFORMATION_CONSTANTS.INSURER_OR_REPRESENTATIVE_NAME
  ),
  customerAddress: buildAddressObject(
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.ADDRESS_LINE_1),
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.ADDRESS_LINE_2),
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.CITY),
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.COUNTRY)
  ),
  customerEmailAddress: getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.CUSTOMER_EMAIL),
  customerPhoneNumber: getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.CUSTOMER_PHONE),
  incidentType: getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.INCIDENT_TYPE),
  incidentDescription: getFieldValue(
    formState,
    CLAIM_INFORMATION_CONSTANTS.DESCRIPTION_OF_INCIDENT
  ),
  incidentOccurredCountry: getFieldValue(
    formState,
    CLAIM_INFORMATION_CONSTANTS.COUNTRY_WHERE_INCIDENT_OCCURRED
  ),
  productPurchaseDate: formatDateForBackend(
    getFieldValue(formState, CLAIM_INFORMATION_CONSTANTS.PRODUCT_PURCHASE_DATE)
  ),
});

// Helper function to build additional information
const buildAdditionalInfo = (formState: FormStateType) => ({
  itemInstalledBy: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.INSTALLATION_SOURCE),
  productListLink: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.PRODUCT_LINK),
  ercAmountOffered: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.ERC_OFFER_AMOUNT),
  itemReturned: getBooleanFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.ITEM_RETURNED),
  amazonLocation: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.AMAZON_LOCATION),
  sitePocDetails: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.RETURN_CENTER_POC),
  productDisposalAdvised: getBooleanFieldValue(
    formState,
    ADDITIONAL_INFORMATION_CONSTANTS.ADVISED_DISPOSAL
  ),
  sellerAddress: buildAddressObject(
    getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.ADDRESS_LINE_1),
    getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.ADDRESS_LINE_2),
    getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.CITY),
    getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.COUNTRY)
  ),
  sellerPhone: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.PHONE),
  sellerEmailAddress: getFieldValue(formState, ADDITIONAL_INFORMATION_CONSTANTS.EMAIL),
  reportedToAmazonPR: getBooleanFieldValue(
    formState,
    ADDITIONAL_INFORMATION_CONSTANTS.REPORTED_TO_PR
  ),
});

// Helper function to build file attachments
const buildFileAttachments = (formState: FormStateType) => ({
  photoAttachments: buildAttachments(formState, FILE_ATTACHMENTS_CONSTANTS.PHOTOS),
  documentAttachments: buildAttachments(formState, FILE_ATTACHMENTS_CONSTANTS.DOCUMENTS),
});

export const createIPLClaimModelBuilder = (
  formState: FormStateType,
  additionalFields: IPLClaimModelAdditionalFields
): CreateInternationalProductLiabilityClaimCommandInput => {
  const createIPLClaimModel: CreateInternationalProductLiabilityClaimCommandInput = {
    ...buildClaimReporterInfo(additionalFields),
    ...buildProductInfo(formState),
    ...buildClaimInfo(formState),
    ...buildAdditionalInfo(formState),
    ...buildFileAttachments(formState),
  };

  return createIPLClaimModel;
};
